import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/apiClient";
import AddressFields, { isAddressComplete } from "@/components/app/AddressFields";
import ResetPasswordDialog from "@/components/app/ResetPasswordDialog";
import { toast } from "sonner";
import { UserPlus, Save } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", document: "" };
const EMPTY_ADDR = { cep: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "" };

export default function CreateCustomerDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => { setForm(EMPTY); setAddr(EMPTY_ADDR); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Preencha nome, e-mail e telefone."); return;
    }
    if (!isAddressComplete(addr)) {
      toast.error("Endereço é obrigatório (CEP, rua, cidade e UF)."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/customers", {
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        document: form.document.trim() || null,
        address: {
          cep: addr.cep, street: addr.street, number: addr.number || null,
          complement: addr.complement || null, neighborhood: addr.neighborhood || null,
          city: addr.city, state: addr.state,
        },
      });
      toast.success("Cliente cadastrado! Senha provisória gerada.");
      setOpen(false);
      reset();
      setResult(data);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
        <DialogTrigger asChild>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="create-customer-button">
            <UserPlus className="h-4 w-4" /> Cadastrar cliente
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="create-customer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#1e7be0]" /> Cadastrar cliente</DialogTitle>
            <DialogDescription>Gera uma senha provisória — o cliente troca no 1º acesso.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5"><Label>Nome completo *</Label>
              <Input value={form.name} onChange={upd("name")} placeholder="Nome do cliente" data-testid="customer-name-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label>E-mail *</Label>
                <Input type="email" value={form.email} onChange={upd("email")} placeholder="cliente@email.com" data-testid="customer-email-input" /></div>
              <div className="space-y-1.5"><Label>Telefone *</Label>
                <Input value={form.phone} onChange={upd("phone")} placeholder="(21) 99999-9999" data-testid="customer-phone-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>CPF / CNPJ (opcional)</Label>
              <Input value={form.document} onChange={upd("document")} placeholder="000.000.000-00" data-testid="customer-document-input" /></div>
            <div className="pt-1">
              <Label className="text-sm font-semibold text-slate-700">Endereço *</Label>
              <div className="mt-2"><AddressFields value={addr} onChange={setAddr} idPrefix="customer-addr" /></div>
            </div>
            <DialogFooter>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5" disabled={busy} data-testid="customer-save-button">
                <Save className="h-4 w-4" /> {busy ? "Cadastrando..." : "Cadastrar e gerar senha"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ResetPasswordDialog open={!!result} onOpenChange={(o) => !o && setResult(null)} result={result} />
    </>
  );
}
